import { useState, useCallback } from 'react';
import { Input } from '@sharpe/ui/catalyst';
import { trpc } from '@/lib/trpc';
import { ArrowRight } from '@carbon/icons-react';
import { motion, AnimatePresence } from 'framer-motion';

interface WaitlistFormProps {
  initialEmail?: string;
}

type FormState = 'idle' | 'loading' | 'sent' | 'error';

export function WaitlistForm({ initialEmail }: WaitlistFormProps) {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [name, setName] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [error, setError] = useState<string | null>(null);

  const joinMutation = trpc.waitlist.join.useMutation();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedEmail = email.trim().toLowerCase();

      const trimmedName = name.trim();
      if (!trimmedEmail || !trimmedName) return;

      setState('loading');
      setError(null);

      try {
        await joinMutation.mutateAsync({
          email: trimmedEmail,
          name: trimmedName,
          source: 'spa',
        });

        setState('sent');
      } catch (err) {
        setState('error');
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
        setError(message);
      }
    },
    [email, name, joinMutation]
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.1,
        delayChildren: 0.15,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.15 },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {state === 'sent' ? (
        <motion.div
          key="success"
          variants={successVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            variants={itemVariants}
            className="rounded-md border border-ds-green-500/20 bg-ds-green-500/5 px-3 py-2.5"
          >
            <p className="text-sm font-medium text-foreground">You're on the waitlist!</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Check your email for confirmation.
            </p>
          </motion.div>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onSubmit={handleSubmit}
          className="space-y-3"
        >
          <motion.div variants={itemVariants}>
            <label className="block text-xs font-medium text-foreground/80 mb-1.5">Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              disabled={state === 'loading'}
              autoComplete="name"
              required
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <label className="block text-xs font-medium text-foreground/80 mb-1.5">Email</label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={state === 'loading'}
                autoComplete="email"
                inputMode="email"
                required
                className="flex-1"
              />
              <motion.button
                type="submit"
                disabled={state === 'loading'}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-ring/30 bg-background dark:bg-sidebar text-foreground transition-colors hover:bg-background/80 dark:hover:bg-sidebar/80 disabled:opacity-50 disabled:pointer-events-none"
              >
                {state === 'loading' ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="block h-4 w-4 rounded-full border-2 border-foreground/20 border-t-foreground"
                  />
                ) : (
                  <ArrowRight size={18} />
                )}
              </motion.button>
            </div>
          </motion.div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="rounded-md border border-ds-red-400 bg-ds-red-100 px-3 py-2 text-xs text-destructive">
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
